import React, { CSSProperties, useEffect, useState } from 'react'
import { EntityData, EntityType } from '../../lib/types'
import { entities as entitiesApi } from '../../lib/api'
import sortResults from './sort'
import Loading from '../loading'

const ResultColors: Record<string, string> = {
    0: 'text-blue-500',
    1: 'text-green-500',
    2: 'text-yellow-500',
    3: 'text-orange-500',
    4: 'text-red-500',
    5: 'text-purple-500',
}

type Props = {
    entities: Array<EntityType>
    text: string
    textChange: (text: string) => void
    readonly?: boolean
    style? : CSSProperties
    onErr: (err: string) => void
    optionClick: (entity: EntityData) => void
}

export default function EntitySearch(props: Props) {
    const { entities, text, textChange, readonly, onErr, optionClick, style = {} } = props
    const [results, setResults] = useState<Array<EntityData>>([])
    const [menuVisible, setMenuVisible] = useState(false)
    const [loading, setLoading] = useState(false)
    const [prevText, setPrevText] = useState<string>(text)

    useEffect(() => {
        const listener = (e: any) => {
            if (!e.target.classList.contains('entity-search')) {
                setMenuVisible(false)
            }
        }
        window.addEventListener('click', listener)
        return () => window.removeEventListener('click', listener)
    }, [])

    useEffect(() => {
        const controller = new AbortController()
        if (text && text.length >= 3 && text !== prevText && !readonly) {
            setLoading(true)
            let promises = entities.map((entity) =>
                entitiesApi.search(entity, text, controller.signal),
            )
            Promise.all(promises)
                .then((allResults) => {
                    allResults.forEach((results, i) => {
                        results.forEach((r) => (r._entityIndex = i))
                    })
                    let results = allResults.reduce((a, b) => a.concat(b), [])
                    setResults(sortResults(results, text))
                    setMenuVisible(true)
                    setLoading(false)
                })
                .catch((err) => !controller.signal.aborted && onErr(err.message || err))
        }

        return () => controller.abort()
    }, [text, entities, textChange, onErr, prevText, readonly])

    function onClick(result: EntityData) {
        setMenuVisible(false)
        setPrevText(result.name)
        optionClick(result)
    }

    return (
        <div className='block'>
            <div className='relative inline-block'>
                <input
                    className='entity-search'
                    type='search'
                    value={text}
                    onChange={(e) => textChange(e.target.value)}
                    readOnly={readonly}
                    style={style}
                    placeholder='Search by name...'
                />
                {loading && <Loading />}
                {menuVisible && (
                    <div className='popdown-menu'>
                        {!results.length && !loading && (
                            <div className='px-2 text-gray-500 text-sm text-center'>No Results</div>
                        )}
                        {results.slice(0, 100).map((result) => (
                            <div
                                className='popdown-item'
                                key={`${result._entityIndex}${result.id}`}
                                onClick={() => onClick(result)}
                            >
                                <span className='text-sm text-gray-500 mr-3'>{result.id}</span>
                                {result.name}
                                {entities.length > 1 && (
                                    <div
                                        className={
                                            'text-sm bg-gray-100 rounded-full text-center opacity-50 ' +
                                            ResultColors[result._entityIndex!]
                                        }
                                    >
                                        {entities[result._entityIndex!].dataset}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
